// src/app/services/cookie-consent.service.ts
import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

export interface ConsentSettings {
  necessary: boolean;  // Immer true
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string;
}

// Standard-Einstellung
const DEFAULT_CONSENT: ConsentSettings = {
  necessary: true,
  preferences: false,
  statistics: false,
  marketing: false,
  timestamp: 0,
  version: '1.0'
};

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly COOKIE_NAME = 'cookie_consent';
  private consentStateSubject = new BehaviorSubject<ConsentSettings>(DEFAULT_CONSENT);
  public consentState$ = this.consentStateSubject.asObservable();
  private isBrowser: boolean;
  private _hasAnswered: boolean = false;
  private showBannerSubject = new BehaviorSubject<boolean>(false);
  public showBanner$ = this.showBannerSubject.asObservable();
  
  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    
    // Initialisiere Cookie-Status nur im Browser
    if (this.isBrowser) {
      try {
        this._hasAnswered = this.hasAnswered();
        if (this._hasAnswered) {
          const settings = this.readConsentSettingsFromCookie();
          this.consentStateSubject.next(settings);
        }
      } catch (e) {
        console.error('Error initializing consent state:', e);
      }
    }
  }
  
  private readConsentSettingsFromCookie(): ConsentSettings {
    if (!this.isBrowser) return DEFAULT_CONSENT;
    
    try {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === this.COOKIE_NAME) {
          try {
            // Versuche JSON zu parsen
            return JSON.parse(decodeURIComponent(value)) as ConsentSettings;
          } catch {
            // Fallback für alte Cookies, die nur true/false waren
            return {
              ...DEFAULT_CONSENT,
              statistics: value === 'true',
              timestamp: Date.now()
            };
          }
        }
      }
      return DEFAULT_CONSENT;
    } catch (e) {
      console.error('Error reading cookie:', e);
      return DEFAULT_CONSENT;
    }
  }
  
  public hasAnswered(): boolean {
    if (!this.isBrowser) return false;
    
    try {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name] = cookie.trim().split('=');
        if (name === this.COOKIE_NAME) {
          return true;
        }
      }
      return false;
    } catch (e) {
      console.error('Error checking if answered:', e);
      return false;
    }
  }
  
  public hasConsented(option: 'necessary' | 'statistics' | 'marketing'): boolean {
    const settings = this.consentStateSubject.value;
    return settings[option] || false; // Zugriff mit Bracket-Notation + Fallback
  }
  
  public acceptConsent(settings?: Partial<ConsentSettings>): void {
    if (!this.isBrowser) return;
    
    try {
      const newSettings: ConsentSettings = {
        ...DEFAULT_CONSENT,
        ...settings,
        necessary: true,
        timestamp: Date.now(),
        version: '1.0'
      };
      
      const date = new Date();
      date.setTime(date.getTime() + (180 * 24 * 60 * 60 * 1000));
      
      // Stelle sicher, dass der Cookie-Wert korrekt formatiert ist
      const cookieValue = encodeURIComponent(JSON.stringify(newSettings));
      
      // Setze den Cookie mit vollständigem Pfad und Domain
      document.cookie = `${this.COOKIE_NAME}=${cookieValue};expires=${date.toUTCString()};path=/;SameSite=Lax`;
      
      // Aktualisiere den BehaviorSubject
      this.consentStateSubject.next(newSettings);
      this._hasAnswered = true;
      
      // Sofortige Überprüfung, ob das Cookie gesetzt wurde
      // console.log('Cookie nach Setzen:', document.cookie.split(';').find(c => c.trim().startsWith(`${this.COOKIE_NAME}=`)));
    } catch (e) {
      console.error('Error accepting consent:', e);
    }
  }
  
  // Für vollständige Akzeptanz aller Kategorien
  public acceptAllConsent(): void {
    this.acceptConsent({
      necessary: true,
      preferences: true,
      statistics: true,
      marketing: true
    });
  }
  
  public declineConsent(): void {
    if (!this.isBrowser) return;
    
    try {
      const newSettings: ConsentSettings = {
        ...DEFAULT_CONSENT,
        timestamp: Date.now(),
        version: '1.0'
      };
      
      const date = new Date();
      date.setTime(date.getTime() + (180 * 24 * 60 * 60 * 1000));
      document.cookie = `${this.COOKIE_NAME}=${encodeURIComponent(JSON.stringify(newSettings))};expires=${date.toUTCString()};path=/;SameSite=Lax`;
      this.consentStateSubject.next(newSettings);
      this._hasAnswered = true;
      
      // Analytics entfernen, wenn die Zustimmung widerrufen wird
      this.removeGoogleAnalytics();
    } catch (e) {
      console.error('Error declining consent:', e);
    }
  }

  public removeGoogleAnalytics(): void {
    if (!this.isBrowser) return;
    
    try {

      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
          // Falls Sie auch andere Google-Dienste (z.B. Ads) nutzen,
          // müssten hier ggf. auch 'ad_storage', 'ad_user_data', 
          // 'ad_personalization' auf 'denied' gesetzt werden.
        });
        console.log('Google Analytics consent status updated to denied.');
      }


      // GA4-Cookies löschen
      const cookiesToDelete = ['_ga', '_ga_*', '_gid', '_gat'];
      
      cookiesToDelete.forEach(cookieName => {
        if (cookieName.includes('*')) {
          // Wildcard-Suche für _ga_*
          const prefix = cookieName.replace('*', '');
          document.cookie.split(';').forEach(cookie => {
            const trimmedCookie = cookie.trim();
            if (trimmedCookie.indexOf(prefix) === 0) {
              const name = trimmedCookie.split('=')[0];
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}; SameSite=Lax`;
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}; SameSite=Lax`;
            }
          });
        } else {
          // Direkte Löschung für andere Cookies
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}; SameSite=Lax`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}; SameSite=Lax`;
        }
      });
      
      // GA4 Script entfernen
      const gaScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
      if (gaScript) {
        gaScript.remove();
      }
      
      // GA4 Inline-Script identifizieren und entfernen
      const scripts = document.querySelectorAll('script');
      scripts.forEach(script => {
        if (script.textContent && script.textContent.includes('gtag')) {
          script.remove();
        }
      });
      
      // GA4 Objekte zurücksetzen
      if (window.dataLayer) {
        window.dataLayer = [];
      }
      
      if (window.gtag) {
        // Definiere gtag neu als leere Funktion
        window.gtag = function() {};
      }
      
      // console.log('Google Analytics wurde entfernt');
    } catch (e) {
      // console.error('Fehler beim Entfernen von Google Analytics:', e);
    }
  }

  public getConsentSettings(): ConsentSettings {
    return this.readConsentSettingsFromCookie();
  }
  
  public resetConsent(): void {
    if (!this.isBrowser) return;
    this._hasAnswered = false;
    this.showBannerSubject.next(true);
  }

  public showConsent(): void {
    if (!this.isBrowser) return;
    this.showBannerSubject.next(true);
  }

  public testCookies():void {
    setInterval(() => {
      // 1. rawCookie = "cookie_consent=%7B...%7D"
      const rawCookie = document.cookie
        .split("; ")
        .find(cookie => cookie.startsWith("cookie_consent="));
      if (!rawCookie) return;
    
      // 2. value rausholen und URL-dekodieren
      const encoded = rawCookie.split("=")[1];
      const decoded = decodeURIComponent(encoded);
    
      // 3. JSON parsen und formatiert ausgeben
      try {
        const obj = JSON.parse(decoded);
        console.clear(); // optional
        console.log("cookie_consent:", JSON.stringify(obj, null, 2));
      } catch (e) {
        console.error("Ungültiges JSON:", decoded);
      }
    }, 500);
  }
}